/* eslint-disable react/prop-types */
import { Button, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { t } from '../i18n/translations';
import { NO_COACH_ID } from '../constants';
import {
    Transition, Subcontainer,
    Summary, SummaryTimes, TimeIcon, Prices, PriceSet, Price,
    DiscrepancyText, Book,
} from './Picker.styles';

/**
 * Right column of the booking widget:
 *   booking summary → pricing → payment type → Book button
 *   + success/error dialogs + coach-alert snackbar
 */
export default function BookingSummary({
    lang,
    showStep, step3Summary, isSynced,
    selectedStart, selectedEnd, timeFrames,
    selectedBoat, selectedCoach,
    totalPrice, paymentType,
    comment, handleChange,
    order, checkoutError,
    successfullyBooked, errorBooking, coachAlert,
    handleClose,
}) {
    return (
        <>
            {/* ── Summary panel ──────────────────────────────────────── */}
            <Subcontainer item xs={10} md={4}>
                <Summary elevation={2}>
                    {showStep !== 'summary' ? (
                        <Grid container direction="column" align="center">
                            <Typography variant="subtitle1" style={{ color: '#666', margin: '30px 0' }}>
                                {t(lang, 'summaryIdleHint')}
                            </Typography>
                        </Grid>
                    ) : (
                        <Grid container direction="column" align="center">
                            {step3Summary && <>
                                <Typography variant="h5" justify="center">{t(lang, 'bookingDetails')}</Typography>

                                <SummaryTimes item>
                                    <TimeIcon />
                                    <Typography variant="subtitle1" style={{ marginRight: 12 }}>
                                        {t(lang, 'start')} {step3Summary.start}
                                    </Typography>
                                    <Typography variant="subtitle1">
                                        {t(lang, 'end')} {step3Summary.end}
                                    </Typography>
                                </SummaryTimes>

                                {!isSynced && (
                                    <DiscrepancyText variant="subtitle2" style={{ background: '#fff3cd', padding: '8px', borderRadius: 4, width: '100%', textAlign: 'center', marginBottom: 8 }}>
                                        {t(lang, 'selectionsChanged')}
                                    </DiscrepancyText>
                                )}

                                {/* Booking details */}
                                <Prices variant="outlined">
                                    <PriceSet container direction="row" justifyContent="space-between"><Price>{t(lang, 'date')}:</Price>         <Price>{step3Summary.date}</Price></PriceSet>
                                    <PriceSet container direction="row" justifyContent="space-between"><Price>{t(lang, 'boat')}:</Price>         <Price>{step3Summary.boat}</Price></PriceSet>
                                    <PriceSet container direction="row" justifyContent="space-between"><Price>{t(lang, 'coach')}:</Price>        <Price>{step3Summary.coach}</Price></PriceSet>
                                    <PriceSet container direction="row" justifyContent="space-between"><Price>{t(lang, 'totalRiders')}:</Price>  <Price>{step3Summary.riders}</Price></PriceSet>
                                    <PriceSet container direction="row" justifyContent="space-between"><Price>{t(lang, 'rookies')}:</Price>      <Price>{step3Summary.beginners}</Price></PriceSet>
                                    <PriceSet container direction="row" justifyContent="space-between"><Price>{t(lang, 'totalDuration')}:</Price><Price>{step3Summary.duration} {t(lang, 'minutes')}</Price></PriceSet>
                                    <br />
                                    <PriceSet container direction="row" justifyContent="space-between"><Price>{t(lang, 'name')}:</Price>  <Price>{step3Summary.name}</Price></PriceSet>
                                    <PriceSet container direction="row" justifyContent="space-between"><Price>{t(lang, 'phone')}:</Price> <Price>{step3Summary.phone}</Price></PriceSet>
                                </Prices>

                                {/* Pricing */}
                                <Prices variant="outlined">
                                    <PriceSet container direction="row" justifyContent="space-between">
                                        <Price>{t(lang, 'balance')}:</Price>
                                        <Price>{step3Summary.balance ?? 0} {t(lang, 'uah')}</Price>
                                    </PriceSet>
                                    {(step3Summary.balance ?? 0) > 0 && <br />}
                                    {selectedStart === '' && (
                                        <PriceSet container direction="row"><Price>{t(lang, 'chooseStartForPrice')}</Price></PriceSet>
                                    )}
                                    {selectedBoat !== NO_COACH_ID && (
                                        <PriceSet container direction="row" justifyContent="space-between">
                                            <Price>{t(lang, 'boatPrice')}:</Price>
                                            <Price>{step3Summary.boatPrice} {t(lang, 'uah')}</Price>
                                        </PriceSet>
                                    )}
                                    {selectedCoach !== NO_COACH_ID && (
                                        <PriceSet container direction="row" justifyContent="space-between">
                                            <Price>{t(lang, 'coachPrice')}:</Price>
                                            <Price>{step3Summary.coachPrice} {t(lang, 'uah')}</Price>
                                        </PriceSet>
                                    )}
                                    {(step3Summary.balance ?? 0) > 0 && (step3Summary.coachPrice > 0 || step3Summary.boatPrice > 0) && (
                                        <PriceSet container direction="row" justifyContent="space-between">
                                            <Price><b>{t(lang, 'totalPrice')}:</b></Price>
                                            <Price>{step3Summary.coachPrice + step3Summary.boatPrice} {t(lang, 'uah')}</Price>
                                        </PriceSet>
                                    )}
                                    <br />
                                    {totalPrice > 0 && (
                                        <PriceSet container direction="row" justifyContent="space-between">
                                            <Price>{t(lang, 'paymentType')}:</Price>
                                            <Price>
                                                <FormControl style={{ marginTop: '-9px' }}>
                                                    <RadioGroup value={paymentType} name="payment-type" onChange={handleChange}>
                                                        <FormControlLabel style={{ marginRight: 'unset' }} value="full" control={<Radio color="primary" />} label={t(lang, 'fullPayment')} />
                                                        <FormControlLabel style={{ marginRight: 'unset' }} value="part" control={<Radio color="primary" />} label={t(lang, 'prepayment50')} />
                                                    </RadioGroup>
                                                </FormControl>
                                            </Price>
                                        </PriceSet>
                                    )}
                                    <br />
                                    <PriceSet container direction="row" justifyContent="space-between">
                                        <Price><b>{t(lang, 'toPay')}:</b></Price>
                                        <Price><b>{totalPrice} {t(lang, 'uah')}</b></Price>
                                    </PriceSet>
                                </Prices>

                                {/* Comment */}
                                <TextField
                                    multiline
                                    style={{ margin: 0, marginTop: 12, width: '100%' }}
                                    value={comment}
                                    name="comment"
                                    onChange={handleChange}
                                    label={t(lang, 'bookingComment')}
                                    variant="outlined"
                                />
                                {comment.length > 0 && (
                                    <Typography variant="caption" display="block" gutterBottom align="right">
                                        {comment.length}/300
                                    </Typography>
                                )}

                                {/* Book button */}
                                <Book variant="contained" onClick={order} disabled={!isSynced || !selectedStart}>
                                    {t(lang, 'book')}
                                </Book>
                                {checkoutError && (
                                    <Typography variant="caption" color="error" display="block" gutterBottom>
                                        {checkoutError}
                                    </Typography>
                                )}

                                {/* Cancellation policy tooltip */}
                                <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                                    <Tooltip
                                        title={
                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                <li>{t(lang, 'cancellationPolicy1')}</li>
                                                <li>{t(lang, 'cancellationPolicy2')}</li>
                                                <li>{t(lang, 'cancellationPolicy3')}</li>
                                            </ul>
                                        }
                                        enterTouchDelay={0}
                                        leaveTouchDelay={5000}
                                        arrow
                                        placement="top"
                                        interactive
                                    >
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            gutterBottom
                                            sx={{
                                                color: 'gray',
                                                borderBottom: '1px dashed gray',
                                                width: 'fit-content',
                                                cursor: 'pointer',
                                                mt: 1, mb: 1,
                                                fontSize: '0.8rem',
                                                userSelect: 'none',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {t(lang, 'cancellationPolicyTitle')}
                                        </Typography>
                                    </Tooltip>
                                </Box>
                            </>}
                        </Grid>
                    )}
                </Summary>
            </Subcontainer>

            {/* ── Dialogs ─────────────────────────────────────────────── */}
            <Dialog open={successfullyBooked} TransitionComponent={Transition} keepMounted onClose={() => handleClose('success')} aria-describedby="success-dialog">
                <DialogTitle>{t(lang, 'successTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="success-dialog">{t(lang, 'successBody')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleClose('success')}>ОК</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={errorBooking} TransitionComponent={Transition} keepMounted onClose={() => handleClose('error')} aria-describedby="error-dialog">
                <DialogTitle>{t(lang, 'errorTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="error-dialog">{t(lang, 'errorBody')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleClose('error')}>ОК</Button>
                </DialogActions>
            </Dialog>

            {/* ── Coach alert snackbar ─────────────────────────────────── */}
            <Snackbar
                open={coachAlert}
                onClose={() => handleClose('coachAlert')}
                TransitionComponent={Transition}
                message={t(lang, 'coachAlertMessage')}
                key="coach-alert"
                autoHideDuration={3000}
            />
        </>
    );
}
